import { FC, Suspense, useCallback, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { Box, Paper, Stack, Typography } from '@mui/material';

import { useRouter } from '@cvt/routing';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import { StepsProgress } from '@shared/components/StepsProgress';

import { OnboardingWizardContext } from '@modules/Onboarding/contexts/OnboardingWizardContext/OnboardingWizardContext';
import { useMe } from '@modules/Auth/hooks/useMe';
import { useUserCrud } from '@modules/Users/hooks/useUserCrud';
import { useStoryCrud } from '@modules/Stories/hooks/useStoryCrud';

import { ObjectivesStep } from './ObjectivesStep';
import { StoryStep } from './StoryStep';
import { ValuesStep } from './ValuesStep';


export const OnboardingWizard: FC = () => {
  const { currentStepIndex, steps, onNextStep, onPreviousStep, data } = useContext(OnboardingWizardContext);
  const router = useRouter();
  const { user } = useMe();

  const { patchUser } = useUserCrud();
  const { createStory } = useStoryCrud();

  const onCompleteOnboarding = useCallback(async () => {

    const preferences = data.preferences || {};

    await patchUser({
      id: user?.id,
      preferences: {
        ...preferences,
      },
    });

    await createStory.mutateAsync({
      user: user?.id!,
      content: data.story!,
    });

  }, [createStory, data, patchUser, user?.id]);

  return (
    <Stack justifyContent="center" alignItems="center" height="100%" width="100%">
      <Box
        sx={{
          backgroundColor: '#fafafb',
          borderRadius: '12px',
          border : '1px solid #e0e1e7',
          boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(29, 35, 48, 0.03) 0px 2px 4px -1px',
          py: 4,
          px: 3,
          maxWidth: 670,
        }}
        component={Paper}
      >
        <Stack gap={2} mb={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={500}>Step {currentStepIndex + 1} of {steps.length}</Typography>
            <Typography variant="body2" color="primary.main" fontWeight={500}>{Math.floor((currentStepIndex + 1) / steps.length * 100)}% complete</Typography>
          </Stack>
          <StepsProgress stepNumber={currentStepIndex + 1} totalSteps={steps.length}/>
        </Stack>
        <Routes>
          <Route path="objectives" element={(
            <Suspense fallback={<BodyLoading height="100%"/>}>
              <ObjectivesStep
                onComplete={onNextStep}
              />
            </Suspense>
          )}/>
          <Route path="values" element={(
            <Suspense fallback={<BodyLoading height="100%"/>}>
              <ValuesStep
                onBack={onPreviousStep}
                onComplete={onNextStep}
              />
            </Suspense>
          )}/>
          <Route path="story" element={(
            <Suspense fallback={<BodyLoading height="100%"/>}>
              <StoryStep
                onComplete={onCompleteOnboarding}
                onBack={onPreviousStep}
              />
            </Suspense>
          )}/>
          <Route path="*" element={<Navigate to={router.onboarding.objectives.path} replace/>}/>
        </Routes>
      </Box>
    </Stack>
  );
};
